import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { helpdeskApiService } from '../services/helpdesk-api.service';
import { helpdeskQueryKeys } from '../services/helpdesk-query-keys';
import {
  ListTicketsPayload,
  CreateTicketPayload,
  UpdateTicketPayload,
  HelpdeskNotification
} from '../types';

const DEFAULT_STALE_TIME = 5 * 60 * 1000;

export const useTicketsQuery = (filters: ListTicketsPayload) =>
  useQuery({
    queryKey: helpdeskQueryKeys.ticketList(filters),
    queryFn: () => helpdeskApiService.listTickets(filters),
    staleTime: 0
  });

export const useTicketDetailQuery = (id: string) =>
  useQuery({
    queryKey: helpdeskQueryKeys.ticketDetail(id),
    queryFn: () => helpdeskApiService.getTicketById(id),
    enabled: !!id,
    staleTime: DEFAULT_STALE_TIME
  });

export const useCommentsQuery = (ticketId: string) =>
  useQuery({
    queryKey: helpdeskQueryKeys.comments(ticketId),
    queryFn: () => helpdeskApiService.getComments(ticketId),
    enabled: !!ticketId,
    staleTime: DEFAULT_STALE_TIME
  });

export const useNotificationsQuery = () =>
  useQuery({
    queryKey: helpdeskQueryKeys.notifications(),
    queryFn: () => helpdeskApiService.getNotifications(),
    refetchInterval: DEFAULT_STALE_TIME
  });

export const useUnreadNotificationCount = (): number => {
  const { data } = useNotificationsQuery();
  if (!data) return 0;
  return data.filter((n: HelpdeskNotification) => !n.read).length;
};

export const useCreateTicketMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTicketPayload) => helpdeskApiService.createTicket(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: helpdeskQueryKeys.tickets() });
    }
  });
};

export const useUpdateTicketMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTicketPayload }) =>
      helpdeskApiService.updateTicket(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: helpdeskQueryKeys.ticketDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: helpdeskQueryKeys.tickets() });
    }
  });
};

export const useCreateCommentMutation = (ticketId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => helpdeskApiService.createComment(ticketId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: helpdeskQueryKeys.comments(ticketId) });
    }
  });
};

export const useMarkNotificationReadMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => helpdeskApiService.markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: helpdeskQueryKeys.notifications() });
    }
  });
};

export const useMarkAllNotificationsReadMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => helpdeskApiService.markAllNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: helpdeskQueryKeys.notifications() });
    }
  });
};

export const useAnalyticsSummaryQuery = () =>
  useQuery({
    queryKey: helpdeskQueryKeys.analytics.summary(),
    queryFn: () => helpdeskApiService.getAnalyticsSummary(),
    staleTime: DEFAULT_STALE_TIME
  });

export const useAnalyticsVolumeQuery = (days: number) =>
  useQuery({
    queryKey: helpdeskQueryKeys.analytics.volume(days),
    queryFn: () => helpdeskApiService.getAnalyticsVolume(days),
    staleTime: DEFAULT_STALE_TIME
  });

export const useAnalyticsByCategoryQuery = () =>
  useQuery({
    queryKey: helpdeskQueryKeys.analytics.byCategory(),
    queryFn: () => helpdeskApiService.getAnalyticsByCategory(),
    staleTime: DEFAULT_STALE_TIME
  });

export const useAnalyticsResolutionTimeQuery = () =>
  useQuery({
    queryKey: helpdeskQueryKeys.analytics.resolutionTime(),
    queryFn: () => helpdeskApiService.getAnalyticsResolutionTime(),
    staleTime: DEFAULT_STALE_TIME
  });

export const useAnalyticsTopResolversQuery = () =>
  useQuery({
    queryKey: helpdeskQueryKeys.analytics.topResolvers(),
    queryFn: () => helpdeskApiService.getAnalyticsTopResolvers(),
    staleTime: DEFAULT_STALE_TIME
  });

export const useSummarizeTicketMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: string) => helpdeskApiService.summarizeTicket(ticketId),
    onSuccess: (_data, ticketId) => {
      queryClient.invalidateQueries({ queryKey: helpdeskQueryKeys.ticketDetail(ticketId) });
    }
  });
};

export const usePolishCommentMutation = () => {
  return useMutation({
    mutationFn: ({ ticketId, draftBody }: { ticketId: string; draftBody: string }) =>
      helpdeskApiService.polishComment(ticketId, draftBody)
  });
};

export const useHelpdeskUsersQuery = (name: string, role?: string[]) =>
  useQuery({
    queryKey: helpdeskQueryKeys.users(name, role),
    queryFn: () => helpdeskApiService.searchHelpdeskUsers(name, role),
    enabled: name.length >= 2,
    staleTime: DEFAULT_STALE_TIME
  });
